import React, { useState } from 'react';
import { RefreshCw, XCircle, Mail, Clock, CheckCircle, Ban } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const STATUS_CONFIG = {
  pending:   { label: 'Bekliyor',   color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  accepted:  { label: 'Kabul Edildi', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  cancelled: { label: 'İptal',       color: 'bg-red-100 text-red-600',      icon: Ban },
  expired:   { label: 'Süresi Doldu', color: 'bg-gray-100 text-gray-500',  icon: XCircle },
};

const daysLeft = (expiresAt) => {
  if (!expiresAt) return 0;
  const diff = new Date(expiresAt) - new Date();
  return Math.max(0, Math.floor(diff / 86400000));
};

const InvitationRow = ({ inv, onResend, onCancel, loading }) => {
  const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  const isPending = inv.status === 'pending';
  const isResendable = inv.status === 'pending' || inv.status === 'expired';
  const days = isPending ? daysLeft(inv.expires_at) : null;

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
      <td className="px-5 py-3.5">
        <div className="font-medium text-gray-900 text-sm">{inv.invitee_name}</div>
        <div className="text-xs text-gray-400 mt-0.5">{inv.email}</div>
        {inv.phone_code && inv.phone_number && (
          <div className="text-xs text-gray-400">{inv.phone_code} {inv.phone_number}</div>
        )}
      </td>
      <td className="px-5 py-3.5">
        <span className="text-xs font-medium bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
          {inv.category_name}
        </span>
      </td>
      <td className="px-5 py-3.5">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 ${cfg.color}`}>
          <Icon className="h-3.5 w-3.5" />
          {cfg.label}
        </span>
        {isPending && days !== null && (
          <div className={`text-xs mt-1 ${days <= 1 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
            {days === 0 ? 'Bugün bitiyor!' : `${days} gün kaldı`}
          </div>
        )}
      </td>
      <td className="px-5 py-3.5 text-xs text-gray-400">
        {inv.created_at ? new Date(inv.created_at).toLocaleDateString('tr-TR') : '—'}
      </td>
      <td className="px-5 py-3.5 text-right">
        <div className="flex items-center justify-end gap-1">
          {isResendable && (
            <Button
              data-testid={`resend-invite-${inv.id}`}
              size="sm"
              variant="ghost"
              onClick={() => onResend(inv.id)}
              disabled={loading}
              className="text-emerald-600 hover:bg-emerald-50 rounded-full text-xs gap-1"
              title="Tekrar Davet Et"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Tekrar
            </Button>
          )}
          {isPending && (
            <Button
              data-testid={`cancel-invite-${inv.id}`}
              size="sm"
              variant="ghost"
              onClick={() => onCancel(inv.id)}
              disabled={loading}
              className="text-red-500 hover:bg-red-50 rounded-full text-xs gap-1"
              title="İptal Et"
            >
              <XCircle className="h-3.5 w-3.5" />
              İptal
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export const InvitationList = ({ invitations, onResend, onCancel, loading }) => {
  if (invitations.length === 0) {
    return (
      <div className="text-center py-14">
        <Mail className="h-12 w-12 text-gray-200 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">Henüz davet gönderilmemiş.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kişi</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kategori</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Durum</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tarih</th>
            <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((inv) => (
            <InvitationRow
              key={inv.id}
              inv={inv}
              onResend={onResend}
              onCancel={onCancel}
              loading={loading}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
